import React, { useState } from "react";
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { getPrComments, getPrDetails } from '../../../services/apiServices';

import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { prDetailsColDef, defaultColDef } from "../../../helpers/GitMetricColumnDef";
import PrDetailsPrComments from './PrDetailsPrComments';
import PrCommentsTableView from "./PrCommentsTableView";
import { prLinkFormatter, processPrForExport } from "../../../helpers/DataHelper";
import CodeBlockModal from "./CodeBlockModal";
import { Col, Row } from "react-bootstrap";
import ExportcommitPrToExcelButton from "../../common/ExportPrToExcelButton";
import moment from "moment";

const PrDetails = ({ usersMetrics, prType, platform, startEndDates }) => {
    const [show, setShow] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [showCodeBlockModal, setShowCodeBlockModal] = useState(false);
    const [modalDisplayComments, setModalDisplayComments] = useState([]);
    const [allPullRequests, setAllPullRequests] = useState([]);
    const [prComments, setPrComments] = useState([]);
    const [commentDisplayedPr, setCommentDisplayedPr] = useState({});
    const [gridApi, setGridApi] = useState(null);

    prDetailsColDef.forEach(column => {
        if (column["field"] === "prCommentCount") {
            column["cellRenderer"] = prCommentRenderer
        }
        if (column["field"] === "prId") {
            column["cellRenderer"] = prIdRenderer
        }
    });

    function prIdRenderer(params) {
        const prDetails = params["data"];
        if (platform) {
            const link = prLinkFormatter(prDetails.commentLink, platform);

            // Function to open link in a new centered window
            const openInCenteredWindow = () => {
                const width = 800;
                const height = 600;
                const left = (window.screen.width - width) / 2;
                const top = (window.screen.height - height) / 2;

                window.open(link, '_blank', `width=${width},height=${height},left=${left},top=${top}`);
            };

            return (
                <a href={link} target="_blank" rel="noreferrer" onClick={(e) => {
                    e.preventDefault(); // Prevent default link behavior
                    openInCenteredWindow(); // Open in centered window
                }}>
                    {prDetails.prId}
                </a>
            );
        } else {
            return (
                <>
                    {prDetails.prId}
                </>
            );
        }
    }


    function prCommentRenderer(params) {
        if (params.value && params.value !== null) {
            const prDetails = params["data"]
            const handlePrCountClick = async (prId) => {
                let allPrComments = await getPrComments({ prIds: [Number(prId)] });
                setPrComments(allPrComments);
                setCommentDisplayedPr(prDetails);

                // select the row programmatically
                if (gridApi) {
                    gridApi.deselectAll();
                    const node = gridApi.getRowNode(prDetails.ID.toString());
                    if (node) {
                        node.setSelected(true);
                    }
                }
            };

            return (<PrDetailsPrComments prDetails={prDetails} handleShow={() => {
                handlePrCountClick(prDetails["ID"]);
                setShowTable(true);
            }} />)
        }
    }

    const handleClose = () => {
        setShow(false)
        setShowTable(false)
    };
    const handleShow = () => {
        setShow(true);
        setShowTable(false);
        handlePrCountClick(usersMetrics["prIds"]);
    };

    const handlePrCountClick = async (prIds) => {
        let prTypes = [];
        if (prType === "totalMergedPrs") {
            prTypes = ["closed", "completed", "MERGED"];
        } else if (prType === "totalDeclinedPrs") {
            prTypes = ["closed", "abandoned", "DECLINED"];
        } else if (prType === "totalOpenPrs") {
            prTypes = ["open", "active", "OPEN"];
        }
        let allPrDetails = await getPrDetails({ prIds, prTypes });
        setAllPullRequests(allPrDetails);
    }

    return (
        <>
            {usersMetrics[prType] > 0 && <Button variant="link" onClick={handleShow}>
                {usersMetrics[prType]}
            </Button>}
            {usersMetrics[prType] === 0 && <span>{usersMetrics[prType]}</span>}
            <Offcanvas show={show} onHide={handleClose} placement="end" className="commit-list-wrapper">
                <Offcanvas.Header closeButton >
                    <Offcanvas.Title className="w-100">
                        <Row className="align-items-center" as={"div"}>
                            <Col xs={10} className="text-truncate">
                                <span className="text-nowrap">Pull Requests By {usersMetrics["developer"]}</span>
                            </Col>
                            <Col xs={2} className="d-flex justify-content-end align-items-center">
                                <ExportcommitPrToExcelButton
                                    titleData={`PRs by ${usersMetrics["developer"]} For Date Range ${moment(startEndDates.startDate).format('ll')} to ${moment(startEndDates.endDate).format('ll')}`}
                                    data={allPullRequests} // Pass the actual data here
                                    filename={`PR-Details: ${usersMetrics["developer"].replace(" ", "")}`}
                                    sheetName="PR-Details"
                                    headers={prDetailsColDef.map(detail => detail.headerName)}
                                    processRowDataArray={processPrForExport}
                                />&nbsp;&nbsp;&nbsp;
                            </Col>
                        </Row>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body style={{ paddingTop: '0px' }}>
                    <Row className={`ag-theme-quartz ${showTable ? "pr-half-detail-grid" : "pr-full-detail-grid"}`}>
                        <AgGridReact
                            rowData={allPullRequests}
                            columnDefs={prDetailsColDef}
                            defaultColDef={defaultColDef}
                            className="ag-grid-style"
                            getRowId={(params) => params.data.ID.toString()}
                            rowSelection={"single"}
                            onGridReady={(params) => setGridApi(params.api)}
                        />
                    </Row>
                    {showTable && <Row>
                        <PrCommentsTableView
                            prComments={prComments}
                            platform={platform}
                            hideTable={() => {
                                setShowTable(false);
                                if (gridApi) {
                                    gridApi.deselectAll();
                                }
                            }}
                            prTitle={commentDisplayedPr.title || ""}
                            handleShowModal={(comments) => {
                                setModalDisplayComments(comments);
                                setShowCodeBlockModal(true);
                            }}
                        />
                    </Row>}
                    <CodeBlockModal
                        show={showCodeBlockModal}
                        handleClose={() => setShowCodeBlockModal(false)}
                        comments={modalDisplayComments}
                    />
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}

PrDetails.propTypes = {
    usersMetrics: PropTypes.object.isRequired,
    prType: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    startEndDates: PropTypes.object.isRequired
}

export default PrDetails;